import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import CardView from '../../../components/Redesing/card-view';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import { IColumn } from '../../../ts/interface/components/redesing/table.interface';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import CouponPicker, {
  type SelectedCoupon,
} from '../../../components/coupons/CouponPicker';
import {
  useUserCouponsByUser,
  type UserCouponByUserGraphQL,
  type UserCouponCouponGraphQL,
  type UserCouponStatusFilter,
} from '../../../hooks/coupons/query/V2';
import {
  useAssignUserCoupon,
  useRevokeUserCoupon,
} from '../../../hooks/coupons/mutation/V2';
import { IAuth } from '../../../ts/interface/admins.interface';
import {
  convertCentsToMoney,
  findAccess,
  getLocaleDateTimeString,
  history,
  timeForHumans,
} from '../../../utils';

interface UserCouponsProps {
  /** The user's legacy oldId (phone) — used as `userOldId`. */
  userOldId: string;
  /** The user's UUID — used as `userId` when granting. */
  userId: string;
  /** System legacy oldId scoping which coupons can be granted. */
  systemOldId?: string;
  documentationLink?: string;
  // Injected from Redux:
  systemAccess: number;
  movaticAccess: number;
  adminId: string;
}

/** Format a coupon's discount for display, matching the coupon list behavior. */
function formatDiscount(coupon?: UserCouponCouponGraphQL | null): string {
  if (!coupon || coupon.discount === null || coupon.discount === undefined) {
    return '—';
  }
  if (coupon.discountType === 'percent') {
    return `${coupon.discount}%`;
  }
  if (coupon.discountType === 'time') {
    return String(timeForHumans(coupon.discount));
  }
  return convertCentsToMoney(coupon.discount);
}

function couponName(coupon?: UserCouponCouponGraphQL | null): string {
  if (!coupon) return '—';
  return coupon.name || coupon.code || '—';
}

const UserCoupons = ({
  userOldId,
  userId,
  systemOldId,
  documentationLink,
  systemAccess,
  movaticAccess,
  adminId,
}: UserCouponsProps) => {
  const canManage = findAccess(systemAccess).includes('rates') || Boolean(movaticAccess);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState('created');

  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<SelectedCoupon | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<UserCouponStatusFilter>('ACTIVE');

  const {
    data: grants,
    isLoading,
    refetch,
  } = useUserCouponsByUser(userOldId ? { userOldId, status: statusFilter } : undefined, {
    enabled: Boolean(userOldId),
  });

  const { assignUserCoupon, isLoading: isAssigning } = useAssignUserCoupon();
  const { revokeUserCoupon } = useRevokeUserCoupon();

  const rows = useMemo(() => grants ?? [], [grants]);

  const closeGrantModal = () => {
    setShowGrantModal(false);
    setSelectedCoupon(null);
  };

  const handleGrant = () => {
    if (!selectedCoupon) return;
    CustomAsyncToast({
      loadingMessage: 'Granting coupon...',
      successMessage: () => 'Coupon granted successfully!',
      errorMessage: 'There was an error granting the coupon.',
      promise: () =>
        assignUserCoupon({
          couponOldId: selectedCoupon.oldId,
          userOldId,
          userId: userId || null,
          assignedBy: adminId || null,
        }).then((res) => {
          if (!res.isSuccess) {
            throw new Error(res.errors[0]?.message || 'Failed to grant the coupon.');
          }
          return true;
        }),
    }).then((r) => {
      if (r) {
        closeGrantModal();
        refetch();
      }
    });
  };

  const handleRevoke = (grant: UserCouponByUserGraphQL) => {
    setRevokingId(grant.id);
    CustomAsyncToast({
      loadingMessage: 'Revoking coupon...',
      successMessage: () => 'Coupon revoked successfully!',
      errorMessage: 'There was an error revoking the coupon.',
      promise: () =>
        revokeUserCoupon(grant.id).then((res) => {
          if (!res.isSuccess) {
            throw new Error(res.errors[0]?.message || 'Failed to revoke the coupon.');
          }
        }),
    }).then(() => {
      setRevokingId(null);
      refetch();
    });
  };

  return (
    <>
      <CardView
        documentationLink={documentationLink}
        headerActions={
          <Stack direction="row" spacing={2} alignItems="center">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={statusFilter}
              onChange={(_e, next: UserCouponStatusFilter | null) => {
                if (next) setStatusFilter(next);
              }}
              aria-label="Filter coupons by status"
              data-id="user-coupons-status-filter"
            >
              <ToggleButton value="ACTIVE" aria-label="Active coupons">
                Active
              </ToggleButton>
              <ToggleButton value="REVOKED" aria-label="Revoked coupons">
                Revoked
              </ToggleButton>
              <ToggleButton value="ALL" aria-label="All coupons">
                All
              </ToggleButton>
            </ToggleButtonGroup>
            <IconButtonMenu
              buttons={[
                {
                  label: 'Grant coupon',
                  onClick: () => setShowGrantModal(true),
                  startIcon: <AddIcon />,
                  // The grant modal's CouponPicker is scoped by systemOldId; in
                  // the org context it's undefined, so the picker query is
                  // disabled and the modal can never populate. Disable grant.
                  disabled: !canManage || !systemOldId,
                  dataId: 'grant-coupon-button',
                },
              ]}
            />
          </Stack>
        }
        content={
          <PaginationTable
            handleSearch={(s) => setSearch(s.target.value)}
            searchText={search}
            sortBy={sortBy}
            onSortChange={(sort) => setSortBy(sort.key)}
            isLoading={isLoading}
            onClickReturnRow
            onRowCLick={(row: UserCouponByUserGraphQL) => {
              const gid = row.coupon?.id;
              if (!gid) return;
              const isOrg =
                row.coupon?.organizationId != null && row.coupon?.organizationId !== '';
              history.push(
                isOrg ? `/organization/billing/coupon/${gid}` : `/billing/coupon/${gid}`
              );
            }}
            columns={
              [
                {
                  id: 'coupon.name',
                  label: 'Coupon',
                  searchable: true,
                  format: (row: UserCouponByUserGraphQL) => couponName(row.coupon),
                },
                {
                  id: 'coupon.code',
                  label: 'Code',
                  searchable: true,
                  format: (row: UserCouponByUserGraphQL) =>
                    row.coupon?.code || row.couponOldId,
                },
                {
                  id: 'discount',
                  label: 'Discount',
                  format: (row: UserCouponByUserGraphQL) => formatDiscount(row.coupon),
                },
                {
                  id: 'assignedBy',
                  label: 'Assigned By',
                  format: (row: UserCouponByUserGraphQL) => row.assignedBy || '—',
                },
                {
                  id: 'created',
                  label: 'Created On',
                  format: (row: UserCouponByUserGraphQL) =>
                    row.created ? getLocaleDateTimeString(row.created) : '—',
                },
                {
                  id: 'timesUsed',
                  label: 'Times Used',
                  format: (row: UserCouponByUserGraphQL) => row.timesUsed ?? 0,
                },
                {
                  id: 'status',
                  label: 'Status',
                  align: 'left',
                  format: (row: UserCouponByUserGraphQL) => (
                    <SeverityPill color={row.removedOn ? 'error' : 'success'}>
                      {row.removedOn ? 'Revoked' : 'Active'}
                    </SeverityPill>
                  ),
                },
                {
                  id: 'actions',
                  label: 'Actions',
                  align: 'right',
                  format: (row: UserCouponByUserGraphQL) => (
                    <IconButtonMenu
                      slice={1}
                      buttons={[
                        {
                          label: 'Revoke',
                          dataId: `coupon-revoke-${row.id}`,
                          disabled:
                            !canManage || Boolean(row.removedOn) || revokingId === row.id,
                          onClick: (e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleRevoke(row);
                          },
                        },
                      ]}
                    />
                  ),
                },
              ] as IColumn[]
            }
            showFilter={false}
            items={rows}
            onPageChange={(_page, pageN) => setPage(pageN)}
            onRowsPerPageChange={(row) => {
              setRowsPerPage(Number(row.target.value));
              setPage(0);
            }}
            page={page}
            rowsPerPage={rowsPerPage}
            searchPlaceholder={'Search user coupons'}
          />
        }
        title={'User Coupons'}
      />
      {showGrantModal && (
        <MovaticCustomModal
          useContentDividers
          open={showGrantModal}
          onClose={closeGrantModal}
          title={'Grant Coupon'}
          content={
            <Stack spacing={2} sx={{ minWidth: 320 }}>
              <CouponPicker
                description={
                  'Search for a coupon applicable to this system to grant to the user.'
                }
                applicableToSystem={systemOldId}
                selectedCoupon={
                  selectedCoupon
                    ? { value: selectedCoupon.oldId, label: selectedCoupon.label }
                    : null
                }
                onChange={setSelectedCoupon}
              />
            </Stack>
          }
          action={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: closeGrantModal,
                  startIcon: <CloseIcon />,
                },
                {
                  label: 'Grant',
                  dataId: 'grant-coupon-submit',
                  disabled: !canManage || !selectedCoupon || isAssigning,
                  onClick: handleGrant,
                  startIcon: <CheckCircleIcon />,
                },
              ]}
            />
          }
        />
      )}
    </>
  );
};

const mapState = (state: { admin: { admin: IAuth; systemAccess: number } }) => ({
  systemAccess: state.admin.systemAccess,
  movaticAccess: state.admin.admin.admin.movaticAccess,
  adminId: state.admin.admin.admin.id ?? '',
});

export default connect(mapState)(UserCoupons);
